import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useGetDiagnosisTreeListQuery, useGetDicListQuery } from '@/api/common';
import AppDropDownTreeMenu from '@/common/components/form-inputs/AppDropDownTreeMenu';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
	allEnumList?: any;
}

const EditMentalHealthInformationForm = forwardRef(
	({ type, setType, details, allEnumList }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		const { data: followUpClinicPsychiatryDicList } = useGetDicListQuery(
			'follow_up_clinic_psychiatry',
			{ refetchOnMountOrArgChange: true }
		);

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			psychiatricDisordersTypeSpecify: Yup.string().when(
				'psychiatricDisordersTypeList',
				{
					is: (psychiatricDisordersTypeList: string) =>
						psychiatricDisordersTypeList &&
						psychiatricDisordersTypeListIsOther(),
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}
			),
			rehabilitationServicesWaitingSpecify: Yup.string().when(
				'rehabilitationServicesWaitingList',
				{
					is: (rehabilitationServicesWaitingList: string) =>
						rehabilitationServicesWaitingList &&
						rehabilitationServicesWaitingListIsOther(),
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchPsychiatricDisordersTypeList = methods.watch(
			'psychiatricDisordersTypeList'
		);
		const watchRehabilitationServicesWaitingList = methods.watch(
			'rehabilitationServicesWaitingList'
		);

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const psychiatricDisordersTypeListIsOther = () => {
			const obj = allEnumList?.data.PsychiatricDisordersTypeEnum.find(
				(item: any) => item.desc == '其他'
			);
			const isDisable = (watchPsychiatricDisordersTypeList || []).find(
				(item: any) => item == obj.code
			);
			if (isDisable) return true;
			return false;
		};
		const rehabilitationServicesWaitingListIsOther = () => {
			const obj = allEnumList?.data.RehabilitationServicesWaitingEnum.find(
				(item: any) => item.desc == '其他'
			);
			const isDisable = (watchRehabilitationServicesWaitingList || []).find(
				(item: any) => item == obj.code
			);
			if (isDisable) return true;
			return false;
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.zh_mentalHealthInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppAutoCompleteMenu
									control={methods.control}
									name='followUpClinicPsychiatry'
									label={t('preAdmission.psychiatricLocation')}
									options={(followUpClinicPsychiatryDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='psychiatricDisordersTypeList'
									label={t('preAdmission.mentalIllnessCategory')}
									options={(
										allEnumList?.data.PsychiatricDisordersTypeEnum || []
									).map((item: any) => {
										return {
											...item,
											id: item.code,
											name: item.desc,
										};
									})}
									handleChange={() => {
										methods.setValue('psychiatricDisordersTypeSpecify', '');
										methods.clearErrors('psychiatricDisordersTypeSpecify');
									}}
								/>
							</Grid>

							<Grid size={6}>
								<AppTextField
									disabled={!psychiatricDisordersTypeListIsOther()}
									control={methods.control}
									name='psychiatricDisordersTypeSpecify'
									label={`${t('preAdmission.mentalIllnessCategoryDetail')}${
										psychiatricDisordersTypeListIsOther() ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='rehabilitationServicesWaitingList'
									label={t('preAdmission.rehabilitationServices')}
									options={(
										allEnumList?.data.RehabilitationServicesWaitingEnum || []
									).map((item: any) => {
										return {
											...item,
											id: item.code,
											name: item.desc,
										};
									})}
									handleChange={() => {
										methods.setValue(
											'rehabilitationServicesWaitingSpecify',
											''
										);
										methods.clearErrors('rehabilitationServicesWaitingSpecify');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!rehabilitationServicesWaitingListIsOther()}
									control={methods.control}
									name='rehabilitationServicesWaitingSpecify'
									label={`${t('preAdmission.rehabilitationServicesDetail')}${
										rehabilitationServicesWaitingListIsOther() ? '*' : ''
									}`}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ marginBottom: '16px' }} />
					</form>
				</FormProvider>
			</>
		);
	}
);

EditMentalHealthInformationForm.displayName = 'EditMentalHealthInformationForm';

export default EditMentalHealthInformationForm;
